import axios from "axios";
import store from "../store";
import router from "../router";

/**
 * API Client with automatic department ID injection
 *
 * This mimics the Postman script behavior:
 * ```javascript
 * const department = data.data?.user?.departments?.first();
 * if (department) {
 *     pm.environment.set('department', department.id)  // Uses record ID
 * }
 * ```
 *
 * Note: In frontend we use department_id (actual department ID) instead of id (record ID)
 *
 * Every API request will automatically include:
 * - Header: X-Active-Department: {department_id}
 * - Query param (GET requests): department_id={department_id}
 */

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: "/api",
  timeout: 300000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Request interceptor to add auth token and department ID
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add selected department ID to requests (mimics Postman script behavior)
    const selectedDepartment = JSON.parse(
      localStorage.getItem("selectedDepartment") || "null",
    );
    if (selectedDepartment && selectedDepartment.department_id) {
      // Add department ID as header (for all requests)
      config.headers["X-Active-Department"] = selectedDepartment.department_id;

      // Also add as query parameter for GET requests (if backend expects it)
      if (config.method === "get") {
        if (!config.params) {
          config.params = {};
        }
      }

      // Optional: Log department being sent (useful for debugging)
      if (process.env.NODE_ENV === "development") {
        //console.log(`📤 API Request with X-Active-Department: ${selectedDepartment.department_id} (${selectedDepartment.department?.name || 'Unknown'})`)
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle different types of errors with Arabic messages
    if (error.response?.status === 401) {
      // Only trigger logout if we're not already logging out
      if (!store.state.isLoggingOut) {
        // Token expired or invalid
        store.dispatch("logout");
        router.push("/login");
      }
    } else if (error.code === "ECONNABORTED") {
      error.message = "انتهت مهلة الاتصال";
    } else if (
      error.code === "ERR_NETWORK" ||
      error.message === "Network Error"
    ) {
      error.message = "تعذر الاتصال بالخادم";
    }

    return Promise.reject(error);
  },
);

export default apiClient;
